import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaCreditCard,
  FaUser,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaIdCard,
  FaShieldAlt,
  FaUniversalAccess,
  FaRocket,
  FaSpinner,
  FaQrcode,
  FaGraduationCap,
  FaUniversity,
  FaHeadset,
  FaFileProtection,
  FaLock
} from "react-icons/fa";

const CourseCheckout = () => {
  const navigate = useNavigate();

  // Component State
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentDetails, setStudentDetails] = useState({
    fullName: "",
    email: "",
    phone: "",
    qualification: "",
    college: "",
    idNumber: "",
    utrNumber: ""
  });
  const [billingPlan, setBillingPlan] = useState("yearly");
  const [paymentMethod, setPaymentMethod] = useState("upi"); 
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const footerMarkerRef = useRef(null);

  // Deployed Apps Script Live Endpoint
  const GOOGLE_SCRIPT_WEBAPP_URL = "https://script.google.com/macros/s/AKfycbzgV_UpdVL6mLYcYmpdAfxh7YuYyTcL5XLHBfRiZqruMK2bhMf3zqXBkszHayK3K-Tivg/exec";

  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      { rootMargin: "0px 0px -20px 0px" }
    );

    if (footerMarkerRef.current) {
      observer.observe(footerMarkerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const formData = new FormData(e.target);
    const errors = {};

    // Core Validation Rules
    if (!formData.get("fullName")) errors.fullName = "Please enter your full legal name";
    if (!formData.get("email")) errors.email = "Please enter a valid email address for credential delivery";
    if (!formData.get("phone") || formData.get("phone").length !== 10) {
      errors.phone = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.get("qualification")) errors.qualification = "Please specify your degree / qualification";
    if (!formData.get("college")) errors.college = "Please specify your college or university name";
    if (!formData.get("state") || formData.get("state") === "Please select...") {
      errors.state = "Please select your State / Union Territory";
    }

    // UTR Code Validation Rules
    if (paymentMethod === "upi") {
      const utr = formData.get("utrNumber");
      if (!utr || utr.length !== 12 || isNaN(utr)) {
        errors.utrNumber = "Please enter the valid 12-digit numeric UTR / Reference Number";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.keys(errors)[0];
      document.getElementsByName(firstError)[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    const payload = {
      fullName: formData.get("fullName"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      qualification: formData.get("qualification"),
      college: formData.get("college"),
      idNumber: formData.get("idNumber") || "N/A",
      utrNumber: paymentMethod === "upi" ? formData.get("utrNumber") : "CARD_PAYMENT",
      billingPlan: billingPlan
    };

    try {
      await fetch(GOOGLE_SCRIPT_WEBAPP_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      setStudentDetails(payload);
      setSubmitted(true);
    } catch (error) {
      console.error("Network synchronization fault:", error);
      alert("Unable to reach secure enrollment logs. Please check your network connectivity and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-hidden text-slate-200 min-h-screen bg-[#050816]">
      
      {/* BACKGROUND GRAPHIC GRADIENTS */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-[120%] h-[120%] bg-gradient-to-r from-orange-500/20 via-yellow-400/10 to-sky-500/20 blur-3xl animate-slowWave"></div>
        <div className="absolute bottom-0 right-0 w-[120%] h-[120%] bg-gradient-to-r from-sky-500/15 via-orange-300/15 to-yellow-400/15 blur-3xl animate-fastWave"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]"></div>

      {/* ================= POST-PAYMENT SUCCESS PORTAL VIEW ================= */}
      {submitted ? (
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
          <div className="max-w-3xl w-full bg-[#121826]/95 border border-white/10 rounded-[2.5rem] p-8 md:p-12 text-center shadow-[0_0_50px_rgba(249,115,22,0.1)] animate-fadeIn">

            {/* Verification Processing Circle */}
            <div className="w-24 h-24 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-6">
              <FaCheckCircle className="text-amber-400 text-6xl animate-pulse" />
            </div>

            {/* Status Headings */}
            <h2 className="text-4xl font-black bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300 bg-clip-text text-transparent">
              Enrollment Submission Received
            </h2>
            <p className="text-slate-300 mt-4 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Thank you for choosing our platform. Your payment reference details have been logged securely into our student registration database.
            </p>

            {/* Instruction Warning Block */}
            <div className="mt-6 bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-4 text-left max-w-2xl mx-auto">
              <p className="text-sm text-yellow-200/90 leading-relaxed">
                <strong>📌 Important Next Steps:</strong> Our accounts team will cross-verify your 12-digit UTR against bank records. Within <strong>1 to 24 hours</strong>, a secure onboarding link containing your learning portal login credentials will be delivered to your inbox.
              </p>
            </div>

            {/* Registered Profile Verification Node */}
            <div className="mt-8 bg-black/40 border border-white/5 rounded-3xl p-6 text-left max-w-2xl mx-auto grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2 border-b border-white/5 pb-3 flex items-center justify-between">
                <h4 className="text-orange-400 font-bold flex items-center gap-2 text-sm uppercase tracking-wider">
                  <FaIdCard /> Registration Summary
                </h4>
                <span className="text-xs bg-slate-800 text-slate-300 px-3 py-1 rounded-full border border-white/10">Status: Awaiting Verification</span>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-slate-500 text-xs">Student Name</p>
                  <p className="font-bold text-white tracking-wide uppercase text-sm">{studentDetails.fullName}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Email Endpoint</p>
                  <p className="text-slate-200 font-medium text-sm truncate">{studentDetails.email}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Affiliated Institution / College</p>
                  <p className="text-slate-300 text-sm">{studentDetails.college}</p>
                </div>
              </div>

              <div className="space-y-3 border-t md:border-t-0 md:border-l border-white/5 pt-3 md:pt-0 md:pl-6">
                <div>
                  <p className="text-slate-500 text-xs">Submitted UTR Code</p>
                  <p className="font-mono text-sm font-bold text-yellow-300 tracking-wider">{studentDetails.utrNumber}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Billing Option</p>
                  <p className="text-slate-300 text-sm capitalize">{studentDetails.billingPlan} Membership Access Plan</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Registered Phone</p>
                  <p className="text-slate-300 text-sm font-mono">+91 {studentDetails.phone}</p>
                </div>
              </div>
            </div>

            {/* Helpdesk Support Section */}
            <div className="mt-8 pt-6 border-t border-white/5 max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400 text-left">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/5 rounded-xl"><FaHeadset className="text-cyan-400 text-base" /></div>
                <div>
                  <p className="font-semibold text-slate-300">Need Instant Assistance?</p>
                  <p>Reach out directly at: <span className="text-slate-300 font-mono">support@powersense.in</span></p>
                </div>
              </div>
              <button 
                onClick={() => navigate("/dashboard")}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-black text-xs uppercase tracking-wider transition-all hover:scale-[1.02]"
              >
                Access Portal Login Gate
              </button>
            </div>

          </div>
        </div>
      ) : (
        <>
          {/* ================= CHECKOUT MAIN ENTRY PANEL ================= */}
          <div className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-10 pt-10 pb-24 grid lg:grid-cols-[1fr_420px] gap-12 items-start">
            
            {/* LEFT COMPONENT LAYER: ENTRY FORMS */}
            <form onSubmit={handleCheckoutSubmit} className="space-y-10 pt-10">
              
              <div>
                <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300 text-transparent bg-clip-text animate-gradientMove bg-[length:200%_200%] leading-tight">
                  Checkout to start learning
                </h1>
                <p className="text-slate-400 mt-2 text-lg">Verify your student credentials and register your structural account payment details below.</p>
              </div>

              {/* MEMBERSHIP SELECTION CARDS */}
              <div className="rounded-[2.5rem] border border-white/10 bg-[#121826]/70 backdrop-blur-2xl p-8 space-y-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FaUniversalAccess className="text-cyan-400" /> Select Subscription Tiers
                </h2>
                <div className="grid md:grid-cols-2 gap-4 pt-2">
                  
                  <div 
                    onClick={() => setBillingPlan("yearly")}
                    className={`p-6 rounded-3xl border cursor-pointer transition-all duration-300 flex items-start justify-between ${
                      billingPlan === "yearly" ? "border-yellow-400 bg-yellow-400/5 shadow-[0_0_25px_rgba(234,179,8,0.1)]" : "border-white/10 bg-black/20"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input type="radio" name="planPreference" checked={billingPlan === "yearly"} onChange={() => setBillingPlan("yearly")} className="mt-1.5 accent-orange-500 scale-125" />
                      <div>
                        <p className="font-bold text-xl text-white">Yearly Access</p>
                        <p className="text-2xl font-black text-yellow-300 mt-1">₹500.00<span className="text-sm font-normal text-slate-400">/mo</span></p>
                        <p className="text-xs text-slate-400 mt-1">₹6,000.00 billed yearly</p>
                      </div>
                    </div>
                    <span className="bg-green-500/20 text-green-400 text-xs font-black px-3 py-1 rounded-full border border-green-500/30 h-fit">Save ₹3,360</span>
                  </div>

                  <div 
                    onClick={() => setBillingPlan("monthly")}
                    className={`p-6 rounded-3xl border cursor-pointer transition-all duration-300 flex items-start justify-between ${
                      billingPlan === "monthly" ? "border-orange-500 bg-orange-500/5 shadow-[0_0_25px_rgba(249,115,22,0.1)]" : "border-white/10 bg-black/20"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input type="radio" name="planPreference" checked={billingPlan === "monthly"} onChange={() => setBillingPlan("monthly")} className="mt-1.5 accent-orange-500 scale-125" />
                      <div>
                        <p className="font-bold text-xl text-white">Monthly Access</p>
                        <p className="text-2xl font-black text-orange-400 mt-1">₹780.00<span className="text-sm font-normal text-slate-400">/mo</span></p>
                        <p className="text-xs text-slate-400 mt-1">Billed month-to-month</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* COMPREHENSIVE STUDENT INFORMATION FIELDS */}
              <div className="rounded-[2.5rem] border border-white/10 bg-[#121826]/70 backdrop-blur-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FaGraduationCap className="text-orange-400" /> Student Profile Details
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">Full Legal Name <span className="text-orange-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"><FaUser /></span>
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Enter full name"
                        className={`w-full pl-10 pr-5 py-3.5 rounded-2xl bg-[#090d16] border text-white placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.fullName ? "border-red-500" : "border-white/5"}`}
                      />
                    </div>
                    {formErrors.fullName && <p className="text-red-400 text-xs px-1">{formErrors.fullName}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">Email Address <span className="text-orange-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"><FaEnvelope /></span>
                      <input
                        type="email"
                        name="email"
                        placeholder="student@example.com"
                        className={`w-full pl-10 pr-5 py-3.5 rounded-2xl bg-[#090d16] border text-white placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.email ? "border-red-500" : "border-white/5"}`}
                      />
                    </div>
                    {formErrors.email && <p className="text-red-400 text-xs px-1">{formErrors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">Mobile Contact Number <span className="text-orange-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"><FaPhoneAlt /></span>
                      <input
                        type="tel"
                        name="phone"
                        maxLength="10"
                        placeholder="10-digit number"
                        className={`w-full pl-10 pr-5 py-3.5 rounded-2xl bg-[#090d16] border text-white placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.phone ? "border-red-500" : "border-white/5"}`}
                      />
                    </div>
                    {formErrors.phone && <p className="text-red-400 text-xs px-1">{formErrors.phone}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">Degree / Specialization <span className="text-orange-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"><FaGraduationCap /></span>
                      <input
                        type="text"
                        name="qualification"
                        placeholder="e.g., B.Tech EEE, Diploma"
                        className={`w-full pl-10 pr-5 py-3.5 rounded-2xl bg-[#090d16] border text-white placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.qualification ? "border-red-500" : "border-white/5"}`}
                      />
                    </div>
                    {formErrors.qualification && <p className="text-red-400 text-xs px-1">{formErrors.qualification}</p>}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">University / College Name <span className="text-orange-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"><FaUniversity /></span>
                      <input
                        type="text"
                        name="college"
                        placeholder="Enter your academic institution full name"
                        className={`w-full pl-10 pr-5 py-3.5 rounded-2xl bg-[#090d16] border text-white placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.college ? "border-red-500" : "border-white/5"}`}
                      />
                    </div>
                    {formErrors.college && <p className="text-red-400 text-xs px-1">{formErrors.college}</p>}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-400 flex items-center justify-between">
                      <span>Roll Number / University Reg ID <span className="text-slate-600 font-normal">(Optional)</span></span>
                    </label>
                    <input
                      type="text"
                      name="idNumber"
                      placeholder="Enter institutional identity number if applicable"
                      className="w-full px-5 py-3.5 rounded-2xl bg-[#090d16] border border-white/5 text-white placeholder-slate-600 focus:outline-none focus:border-orange-500"
                    />
                  </div>

                </div>
              </div>

              {/* REGIONAL LOCALIZATION SEGMENT */}
              <div className="rounded-[2.5rem] border border-white/10 bg-[#121826]/70 backdrop-blur-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FaMapMarkerAlt className="text-yellow-400" /> Billing address
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">Country</label>
                    <select disabled className="w-full px-5 py-3.5 rounded-2xl bg-black/50 border border-white/5 text-slate-400 cursor-not-allowed">
                      <option>India</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">State / Union Territory</label>
                    <div className="relative">
                      <select
                        name="state"
                        defaultValue="Please select..."
                        className={`w-full px-5 py-3.5 rounded-2xl bg-[#090d16] border text-white focus:outline-none focus:border-orange-500 appearance-none cursor-pointer ${formErrors.state ? "border-red-500" : "border-white/5"}`}
                      >
                        <option disabled value="Please select...">Please select...</option>
                        {indianStates.map((st) => <option key={st} value={st} className="bg-[#121826]">{st}</option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">▼</div>
                    </div>
                    {formErrors.state && <p className="text-red-400 text-xs px-1">{formErrors.state}</p>}
                  </div>
                </div>
              </div>

              {/* PAYMENT REGISTRATION PANEL (UPI RECONCILIATION) */}
              <div className="rounded-[2.5rem] border border-white/10 bg-[#121826]/70 backdrop-blur-2xl p-8 space-y-6">
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <FaCreditCard className="text-cyan-400" /> Payment & Verification Method
                  </h2>
                  <span className="flex items-center gap-1.5 text-xs text-green-400 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 font-medium">
                    <FaShieldAlt /> 256-Bit SSL Encrypted
                  </span>
                </div>

                <div className="flex gap-4 p-1.5 bg-black/40 rounded-2xl border border-white/5">
                  <button type="button" onClick={() => setPaymentMethod("upi")} className={`flex-1 py-3 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${paymentMethod === "upi" ? "bg-[#1f293d] text-white shadow-lg border border-white/5" : "text-slate-400"}`}>
                    <FaQrcode /> UPI Direct QR Activation
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("card")} className={`flex-1 py-3 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${paymentMethod === "card" ? "bg-[#1f293d] text-white shadow-lg border border-white/5" : "text-slate-400"}`}>
                    Credit / Debit Cards
                  </button>
                </div>

                {paymentMethod === "upi" ? (
                  <div className="space-y-6 pt-2 animate-fadeIn">
                    <div className="p-6 bg-black/40 rounded-3xl border border-white/5 flex flex-col md:flex-row items-center gap-6">
                      
                      {/* Live Merchant QR Generation Engine code block wrapper */}
                      <div className="w-40 h-40 bg-white rounded-2xl p-2 flex items-center justify-center shrink-0 shadow-xl relative group">
                        <img 
                          src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=upi://pay?pa=powersense.iit@gmail.com%26pn=PowerSense%26am=6000%26cu=INR" 
                          alt="Secure Payment Gateway QR Code" 
                          className="w-full h-full object-contain"
                        />
                      </div>

                      <div className="space-y-2 text-center md:text-left">
                        <h4 className="text-lg font-bold text-white flex items-center justify-center md:justify-start gap-2">
                          Scan and Authorize Payment
                        </h4>
                        <p className="text-xs text-slate-400 leading-relaxed">
                          Open any mobile payment client (Google Pay, PhonePe, Paytm, BHIM, or your official bank application). Scan the system QR module and fulfill the checkout value.
                        </p>
                        <p className="text-xs font-semibold text-yellow-400 font-mono bg-yellow-500/10 px-3 py-1 rounded-md border border-yellow-500/20 w-fit mx-auto md:mx-0">
                          Official Corporate ID: powersense.iit@gmail.com
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-200">12-Digit Transaction UTR / Reference Number <span className="text-orange-400">*</span></label>
                      <input
                        type="text"
                        name="utrNumber"
                        maxLength="12"
                        placeholder="Enter the 12-digit transaction UTR reference code"
                        className={`w-full px-5 py-3.5 rounded-2xl bg-black/50 border font-mono tracking-wider text-yellow-300 placeholder-slate-600 focus:outline-none focus:border-orange-500 ${formErrors.utrNumber ? "border-red-500" : "border-white/5"}`}
                      />
                      {formErrors.utrNumber ? (
                        <p className="text-red-400 text-xs px-1">{formErrors.utrNumber}</p>
                      ) : (
                        <p className="text-[11px] text-slate-500 italic">Please copy the 12-digit numerical reference string directly from your payment completion interface to activate administrative review lines.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pt-2 animate-fadeIn">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-300">Card number</label>
                      <input type="text" name="cardNumber" placeholder="1234 5678 9012 3456" className="w-full px-5 py-3.5 rounded-2xl bg-black/40 border border-white/5 text-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><label className="text-sm font-semibold text-slate-300">Expiry date</label><input type="text" name="expiryDate" placeholder="MM/YY" className="w-full px-5 py-3.5 rounded-2xl bg-black/40 border border-white/5 text-white" /></div>
                      <div className="space-y-2"><label className="text-sm font-semibold text-slate-300">CVC/CVV</label><input type="password" name="cvc" placeholder="CVC" className="w-full px-5 py-3.5 rounded-2xl bg-black/40 border border-white/5 text-white" /></div>
                    </div>
                    <div className="space-y-2"><label className="text-sm font-semibold text-slate-300">Name on card</label><input type="text" name="cardName" placeholder="Name on card" className="w-full px-5 py-3.5 rounded-2xl bg-black/40 border border-white/5 text-white" /></div>
                  </div>
                )}
              </div>
            </form>

            {/* STICKY RIGHT COLUMN LAYOUT MATRIX */}
            <aside className="hidden lg:block w-[420px] relative min-h-full self-stretch">
              <div className={`w-[420px] rounded-[2.5rem] overflow-hidden border border-white/10 bg-[#121826]/95 backdrop-blur-2xl shadow-[0_0_80px_rgba(255,255,255,0.06)] hover:shadow-[0_0_80px_rgba(249,115,22,0.12)] transition-all duration-500 max-h-[calc(100vh-9rem)] overflow-y-auto custom-scrollbar z-30 ${isFooterVisible ? "absolute bottom-0" : "fixed top-28"}`}>
                
                <div className="relative overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1200&auto=format&fit=crop" alt="Checkout Preview Banner" className="w-full h-[150px] object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121826] via-transparent to-transparent"></div>
                </div>

                <div className="p-8 space-y-6">
                  <div>
                    <span className="text-xs font-bold text-orange-400 tracking-widest uppercase block mb-1">Course Summary</span>
                    <h3 className="text-2xl font-black text-white leading-tight">Professional Certification Training Pass</h3>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-black/30 rounded-2xl border border-white/5">
                    <div>
                      <p className="text-xs text-slate-400">Access Duration</p>
                      <p className="text-base font-bold text-white capitalize">{billingPlan} Billing Tier</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-yellow-300">{billingPlan === "yearly" ? "₹6,000" : "₹780"}</p>
                      <p className="text-xs text-slate-500">{billingPlan === "yearly" ? "/ billed yearly" : "/ billed monthly"}</p>
                    </div>
                  </div>

                  {/* Guaranteed Security Microcopy badge */}
                  <div className="flex items-center gap-2.5 text-xs text-slate-400 bg-black/20 p-3.5 rounded-xl border border-white/5">
                    <FaLock className="text-cyan-400 text-sm shrink-0" />
                    <span>Your transaction parameters are checked via audited corporate bank records.</span>
                  </div>

                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      const nativeFormElement = document.querySelector("form");
                      if (nativeFormElement) nativeFormElement.requestSubmit();
                    }}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500 text-black font-black text-lg hover:scale-[1.02] transition-all duration-500 shadow-[0_10px_40px_rgba(249,115,22,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin" /> Transmitting Audit Logs...
                      </>
                    ) : (
                      "Submit Enrollment Form"
                    )}
                  </button>

                  <p className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-white/5 text-center">
                    🔒 Protected secure corporate checkout pipeline.
                  </p>
                </div>
              </div>
            </aside>

          </div>

          <div ref={footerMarkerRef} className="w-full h-1 pointer-events-none" />
        </>
      )}

      {/* STYLES AND TRANSITION LIFECYCLES */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        @keyframes slowWave {
          0% { transform: translate(-10%, -10%) rotate(0deg); }
          50% { transform: translate(10%, 10%) rotate(14deg); }
          100% { transform: translate(-10%, -10%) rotate(0deg); }
        }
        @keyframes fastWave {
          0% { transform: translate(10%, 10%) rotate(0deg); }
          50% { transform: translate(-10%, -10%) rotate(-14deg); }
          100% { transform: translate(10%, 10%) rotate(0deg); }
        }
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradientMove { animation: gradientMove 6s ease infinite; }
        .animate-slowWave { animation: slowWave 14s ease-in-out infinite; }
        .animate-fastWave { animation: fastWave 10s ease-in-out infinite; }
        .animate-fadeIn { animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </div>
  );
};

export default CourseCheckout;